import { Loader2, Wallet } from 'lucide-react';
import { formatCurrency, isPositiveAmount } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { PaymentMethodCard } from '../vendors/payment-methods/PaymentMethodCard';
import { useClientExpensePaymentMethods } from '../../hooks/usePortal';
import type { ClientExpenseItem } from '../../api/portal';

export interface ClientExpensePaymentModalProps {
  eventId: string;
  /** The line being paid; null keeps the dialog closed. */
  item: ClientExpenseItem | null;
  /** Names the event in the note a pay link carries. */
  eventName?: string;
  onClose: () => void;
}

/**
 * How the client pays one expense line: the payee's payment methods, read-only,
 * with the line's open balance prefilled on any Venmo or PayPal link. A line the
 * office fronted is paid back to the office, and the dialog says so — the vendor
 * has already been paid.
 */
export function ClientExpensePaymentModal({
  eventId,
  item,
  eventName,
  onClose,
}: ClientExpensePaymentModalProps) {
  const { data, isLoading, isError } = useClientExpensePaymentMethods(eventId, item?.id ?? null);
  const lineName = item?.itemName || item?.vendorCategoryName || 'this expense';
  const owed = data?.amount != null && isPositiveAmount(data.amount) ? data.amount : null;
  const note = [eventName, lineName].filter(Boolean).join(' — ');

  return (
    <EntityModal
      open={item !== null}
      onClose={onClose}
      title={data ? `Pay ${data.payeeName}` : 'Pay'}
      size="sm"
    >
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 size={24} className="animate-spin text-primary-600" aria-label="Loading" />
        </div>
      ) : isError || !data ? (
        <p className="text-sm text-gray-500 py-6 text-center">
          The payment details could not be loaded.
        </p>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
            <p className="font-medium text-gray-900">{lineName}</p>
            {data.owedToProducer && (
              <p className="mt-1 text-amber-700">
                The office paid this vendor on your behalf, so this balance is paid back to{' '}
                {data.payeeName}.
              </p>
            )}
            {owed != null && (
              <p className="mt-1">
                Open balance: <strong>{formatCurrency(owed)}</strong>
              </p>
            )}
          </div>

          {data.paymentMethods.length === 0 ? (
            <div className="flex flex-col items-center gap-1 py-6 text-center text-sm text-gray-500">
              <Wallet size={20} className="text-gray-400" aria-hidden="true" />
              No payment details on file — ask the office how to pay.
            </div>
          ) : (
            <ul className="space-y-2" aria-label="Payment methods">
              {data.paymentMethods.map((method) => (
                <PaymentMethodCard
                  key={method.id}
                  method={method}
                  payment={{ amount: owed, note }}
                  audience="client"
                />
              ))}
            </ul>
          )}
          <p className="text-xs text-gray-500">
            Paying through a link does not mark the expense paid here — the office records it once
            the money arrives.
          </p>
        </div>
      )}
    </EntityModal>
  );
}
